export interface InteractiveSessionState {
    code: string;
    participants_count: number;
    participants: string[];
    current_question?: {
        type: 'word_cloud' | 'mcq' | 'short_answer';
        data: any;
        timestamp: string;
    };
    responses: Array<{
        user: string;
        answer: any;
        timestamp: string;
    }>;
}

// Dùng CHUNG base với toàn app (API_CONFIG.python.apiUrl): prod = '/api' (qua nginx → backend đã deploy),
// dev = http://localhost:8000. Trước đây dùng VITE_API_BASE_URL riêng → khi build prod không set sẽ trỏ
// nhầm localhost:8000 → /active/parse-docx & WebSocket hỏng trên live (parse-docx rơi về fallback yếu).
import { API_CONFIG } from '../../config/api.config';

const API_BASE_URL = API_CONFIG.python.apiUrl;   // '/api' (prod) | 'http://localhost:8000' (dev)
// WS tuyệt đối: base tương đối ('/api') → ghép từ origin hiện tại; base http → đổi sang ws/wss.
const WS_BASE_URL = API_BASE_URL.startsWith('http')
  ? API_BASE_URL.replace(/^http/, 'ws')
  : `${typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${typeof window !== 'undefined' ? window.location.host : 'localhost:8000'}${API_BASE_URL}`;

export const InteractiveService = {
    // HTTP Methods
    async createSession(hostId: string = 'teacher'): Promise<string> {
        const res = await fetch(`${API_BASE_URL}/active/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ host_id: hostId })
        });
        if (!res.ok) throw new Error('Failed to create session');
        const data = await res.json();
        return data.code;
    },

    // Đọc .docx phía server (công thức MathType/OMML→LaTeX, tách ảnh) → câu hỏi cho phiên trực tiếp
    async parseDocx(file: File): Promise<{ questions: any[]; count: number }> {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_BASE_URL}/active/parse-docx`, { method: 'POST', body: fd });
        if (!res.ok) throw new Error(`parse-docx ${res.status}`);
        return res.json();
    },

    // ── Lưu & xem lại kết quả phiên live (cần token GV) ──
    async saveResults(payload: { code?: string; title?: string; scores: Record<string, number>; results: any[]; participants_count: number }): Promise<{ ok: boolean; id: string }> {
        const tk = localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || '';
        const res = await fetch(`${API_BASE_URL}/active/results`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`save ${res.status}`);
        return res.json();
    },
    async listResults(): Promise<any[]> {
        const tk = localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || '';
        const res = await fetch(`${API_BASE_URL}/active/results`, { headers: { Authorization: `Bearer ${tk}` } });
        if (!res.ok) throw new Error(`list ${res.status}`);
        return res.json();
    },
    async getResults(rid: string): Promise<any> {
        const tk = localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || '';
        const res = await fetch(`${API_BASE_URL}/active/results/${rid}`, { headers: { Authorization: `Bearer ${tk}` } });
        if (!res.ok) throw new Error(`get ${res.status}`);
        return res.json();
    },

    async checkSession(code: string): Promise<boolean> {
        try {
            const res = await fetch(`${API_BASE_URL}/active/check/${code}`);
            return res.ok;
        } catch {
            return false;
        }
    },

    // WebSocket Methods
    connectHost(code: string, onUpdate: (state: InteractiveSessionState) => void): WebSocket {
        const ws = new WebSocket(`${WS_BASE_URL}/active/ws/host/${code}`);

        ws.onmessage = (event) => {
            const msg = JSON.parse(event.data);
            if (msg.type === 'SESSION_UPDATE') {
                onUpdate(msg.payload);
            }
        };

        return ws;
    },

    connectStudent(code: string, name: string, onQuestion: (question: any) => void, onLocked?: () => void): WebSocket {
        const ws = new WebSocket(`${WS_BASE_URL}/active/ws/student/${code}/${encodeURIComponent(name)}`);

        ws.onmessage = (event) => {
            const msg = JSON.parse(event.data);
            if (msg.type === 'NEW_QUESTION') {
                onQuestion(msg.payload);
            } else if (msg.type === 'WAITING') {
                onQuestion(null); // Clear active question
            } else if (msg.type === 'LOCKED') {
                onLocked?.();
            }
        };

        return ws;
    }
};
